/**
 * Phase 5F.1 — Admin Learning Plan Builder.
 *
 * - Multi-slot subjects: a subject can have MANY weekly slots (Maths Wed 6PM + Sat 3PM).
 * - Plan-level fields: program, start_date, end_date, timezone.
 * - Per-plan actions: Custom Payment Link, Send Link, Timetable PDF, Edit, Duplicate, Delete.
 * - Bulk reset (DELETE TEST PLANS guard).
 */
import React, { useEffect, useState } from 'react';
import axios from 'axios';
import { toast } from 'sonner';
import {
  Plus, Trash2, Loader2, Link as LinkIcon, BookOpen, Copy, Edit3,
  AlertTriangle, FileDown, CreditCard,
} from 'lucide-react';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const ax = axios.create({ baseURL: BACKEND_URL, withCredentials: true });
// Phase 5F.2 fix — auto-redirect to admin login on stale session
ax.interceptors.response.use(r => r, err => {
  if (err?.response?.status === 401) {
    try {
      toast.error('Session expired — please sign in again');
      const next = encodeURIComponent(window.location.pathname);
      setTimeout(() => { window.location.href = `/admin/login?next=${next}`; }, 1200);
    } catch (_e) { /* ignore */ }
  }
  return Promise.reject(err);
});

const DAYS = ['Monday', 'Tuesday', 'Wednesday', 'Thursday', 'Friday', 'Saturday', 'Sunday'];

const blankSlot = () => ({ day_of_week: 'Wednesday', start_time: '18:00' });

const blankSubject = () => ({
  subject: 'Mathematics',
  tutor_email: '',
  duration_minutes: 30,
  package_type: 'individual',
  slots: [blankSlot()],
});

const AdminLearningPlansPanel = () => {
  const [plans, setPlans] = useState([]);
  const [students, setStudents] = useState([]);
  const [tutors, setTutors] = useState([]);
  const [showCreate, setShowCreate] = useState(false);
  const [showBulkDelete, setShowBulkDelete] = useState(false);
  const [showCustomLink, setShowCustomLink] = useState(null); // plan
  const [showEdit, setShowEdit] = useState(null); // plan
  const [expanded, setExpanded] = useState({});
  const [busy, setBusy] = useState(false);

  const load = async () => {
    try {
      const [p, st, tu] = await Promise.all([
        ax.get('/api/admin/learning-plans'),
        ax.get('/api/admin/users?role=student'),
        ax.get('/api/admin/users?role=tutor'),
      ]);
      setPlans(p.data?.items || []);
      setStudents(st.data?.items || st.data?.users || []);
      setTutors(tu.data?.items || tu.data?.users || []);
    } catch (_e) { toast.error('Could not load Learning Plans'); }
  };
  useEffect(() => { load(); }, []);

  const sendLink = async (id) => {
    try {
      const r = await ax.post(`/api/admin/learning-plans/${id}/send-link`);
      const sentTo = (r.data?.sent_to || []).join(', ');
      if (r.data?.payment_link) toast.success(`Payment link generated${sentTo ? ` · emailed ${sentTo}` : ''}`);
      else toast.warning(`Link skipped: ${r.data?.skipped_reason || 'unknown'}`);
      load();
    } catch (e) { toast.error(e.response?.data?.detail || 'Send failed'); }
  };

  const openPdf = async (id) => {
    try {
      const r = await ax.get(`/api/admin/learning-plans/${id}/timetable.pdf`, { responseType: 'blob' });
      const url = window.URL.createObjectURL(new Blob([r.data], { type: 'application/pdf' }));
      window.open(url, '_blank');
    } catch (_e) { toast.error('Could not download plan PDF'); }
  };

  const duplicate = async (id) => {
    try {
      await ax.post(`/api/admin/learning-plans/${id}/duplicate`);
      toast.success('Plan duplicated');
      load();
    } catch (_e) { toast.error('Duplicate failed'); }
  };

  const deletePlan = async (id) => {
    if (!window.confirm('Are you sure you want to permanently delete this Learning Plan? Paid records will be preserved.')) return;
    try {
      const r = await ax.delete(`/api/admin/learning-plans/${id}`);
      toast.success(`Deleted (${r.data?.series_deleted || 0} series, ${r.data?.bookings_deleted || 0} bookings)`);
      load();
    } catch (_e) { toast.error('Delete failed'); }
  };

  const migrate = async () => {
    setBusy(true);
    try {
      const r = await ax.post('/api/admin/migrations/learning-plans?dry_run=false');
      toast.success(`Migrated: ${r.data.plans_created} created, ${r.data.plans_updated} updated`);
      load();
    } catch (_e) { toast.error('Migration failed'); }
    finally { setBusy(false); }
  };

  return (
    <div className="bg-white rounded-2xl border border-gray-200 p-4" data-testid="admin-learning-plans-panel">
      <div className="flex items-center justify-between mb-4 flex-wrap gap-2">
        <div>
          <h2 className="text-lg font-bold flex items-center gap-2"><BookOpen className="h-5 w-5 text-red-700" /> Learning Plans</h2>
          <p className="text-xs text-gray-500 mt-0.5">One student · many subjects · multi-slot · one timetable.</p>
        </div>
        <div className="flex gap-2 flex-wrap">
          <button onClick={() => setShowBulkDelete(true)} className="text-xs bg-rose-50 hover:bg-rose-100 text-rose-800 border border-rose-200 px-3 py-1.5 rounded-full font-bold uppercase tracking-wider inline-flex items-center gap-1" data-testid="lp-bulk-reset">
            <AlertTriangle className="h-3 w-3" /> Clear Test Plans
          </button>
          <button onClick={migrate} disabled={busy} className="text-xs bg-gray-100 hover:bg-gray-200 px-3 py-1.5 rounded-full font-bold uppercase tracking-wider disabled:opacity-40" data-testid="lp-migrate">{busy ? 'Migrating…' : 'Migrate Legacy'}</button>
          <button onClick={() => setShowCreate(true)} className="text-xs bg-red-700 hover:bg-red-800 text-white px-3 py-1.5 rounded-full font-bold uppercase tracking-wider inline-flex items-center gap-1" data-testid="lp-new">
            <Plus className="h-3 w-3" /> New Plan
          </button>
        </div>
      </div>

      <div className="bg-white border border-gray-200 rounded-xl divide-y divide-gray-100">
        {plans.length === 0 && <div className="px-3 py-6 text-xs italic text-gray-400">No plans yet — create one or run the migration.</div>}
        {plans.map(p => {
          const isOpen = !!expanded[p.plan_id];
          // Group subjects by (subject, tutor) to show multi-slot subjects neatly
          const groups = {};
          (p.subjects || []).forEach(s => {
            const k = `${s.subject}__${s.tutor_email}`;
            if (!groups[k]) groups[k] = { subject: s.subject, tutor: s.tutor_email?.split('@')[0], slots: [] };
            groups[k].slots.push(`${s.day_of_week} ${s.start_time}`);
          });
          return (
            <div key={p.plan_id} className="px-3 py-3 text-xs" data-testid={`plan-row-${p.plan_id}`}>
              <div className="flex justify-between items-start gap-3 flex-wrap">
                <div className="min-w-0 flex-1">
                  <div className="font-bold text-sm text-gray-900">{p.student_name}</div>
                  <div className="text-[10px] text-gray-500">{p.student_email} {p.program ? `· ${p.program}` : ''} · {p.subject_count} subjects · {p.tutor_count} tutors</div>
                  {p.plan_name && <div className="text-[10px] text-gray-700 italic mt-0.5">{p.plan_name}</div>}
                  <div className="mt-2 space-y-1">
                    {Object.values(groups).map((g, idx) => (
                      <div key={idx} className="bg-gray-50 border border-gray-200 rounded-lg px-2 py-1.5">
                        <div className="font-bold text-gray-900 text-[11px]">{g.subject} <span className="font-normal text-gray-500">· {g.tutor}</span></div>
                        <div className="text-[10px] text-gray-600">{g.slots.join('  ·  ')}</div>
                      </div>
                    ))}
                  </div>
                  <button onClick={() => setExpanded(s => ({ ...s, [p.plan_id]: !isOpen }))} className="mt-2 text-[10px] text-red-700 hover:underline">{isOpen ? '▾ Hide actions' : '▸ More actions'}</button>
                </div>
                <div className="text-right shrink-0">
                  <div className="text-sm font-bold text-gray-900">AUD {((p.total_amount_aud_cents || 0) / 100).toFixed(2)}</div>
                  <div className={`text-[10px] font-bold ${p.payment_status === 'paid' ? 'text-emerald-700' : p.payment_status === 'partial' ? 'text-amber-700' : 'text-rose-700'}`}>{p.payment_status?.toUpperCase()}</div>
                </div>
              </div>
              {isOpen && (
                <div className="mt-2 pt-2 border-t border-gray-100 flex flex-wrap gap-1.5" data-testid={`plan-actions-${p.plan_id}`}>
                  <ActionBtn onClick={() => setShowCustomLink(p)} testid={`lp-custom-${p.plan_id}`} icon={CreditCard}>Custom Link</ActionBtn>
                  {p.payment_status !== 'paid' && (
                    <ActionBtn onClick={() => sendLink(p.plan_id)} testid={`lp-send-${p.plan_id}`} icon={LinkIcon} className="bg-amber-600 text-white hover:bg-amber-700">Send Default Link</ActionBtn>
                  )}
                  <ActionBtn onClick={() => openPdf(p.plan_id)} testid={`lp-pdf-${p.plan_id}`} icon={FileDown} className="bg-gray-900 text-white hover:bg-black">Timetable PDF</ActionBtn>
                  <ActionBtn onClick={() => setShowEdit(p)} testid={`lp-edit-${p.plan_id}`} icon={Edit3}>Edit</ActionBtn>
                  <ActionBtn onClick={() => duplicate(p.plan_id)} testid={`lp-dup-${p.plan_id}`} icon={Copy}>Duplicate</ActionBtn>
                  <ActionBtn onClick={() => deletePlan(p.plan_id)} testid={`lp-del-${p.plan_id}`} icon={Trash2} className="bg-rose-50 text-rose-800 border border-rose-200 hover:bg-rose-100">Delete</ActionBtn>
                </div>
              )}
            </div>
          );
        })}
      </div>
      {showCreate && <CreatePlanModal onClose={() => setShowCreate(false)} onDone={() => { setShowCreate(false); load(); }} students={students} tutors={tutors} />}
      {showCustomLink && <CustomLinkModal plan={showCustomLink} onClose={() => setShowCustomLink(null)} onDone={() => { setShowCustomLink(null); load(); }} />}
      {showEdit && <EditPlanModal plan={showEdit} onClose={() => setShowEdit(null)} onDone={() => { setShowEdit(null); load(); }} />}
      {showBulkDelete && <BulkDeleteModal onClose={() => setShowBulkDelete(false)} onDone={() => { setShowBulkDelete(false); load(); }} />}
    </div>
  );
};

const ActionBtn = ({ onClick, testid, children, icon: Icon, className = '' }) => (
  <button onClick={onClick} data-testid={testid}
    className={`text-[10px] px-2.5 py-1 rounded-full font-bold uppercase tracking-wider inline-flex items-center gap-1 ${className || 'bg-gray-100 hover:bg-gray-200 text-gray-800'}`}>
    {Icon && <Icon className="h-2.5 w-2.5" />}{children}
  </button>
);

const CreatePlanModal = ({ onClose, onDone, students, tutors }) => {
  const today = new Date().toISOString().slice(0, 10);
  const [studentEmail, setStudentEmail] = useState(students[0]?.email || '');
  const [parentEmail, setParentEmail] = useState('');
  const [planName, setPlanName] = useState('');
  const [program, setProgram] = useState('Selective Entry');
  const [startDate, setStartDate] = useState(today);
  const [endDate, setEndDate] = useState('');
  const [subjects, setSubjects] = useState([blankSubject()]);
  const [busy, setBusy] = useState(false);
  const [subjectTutors, setSubjectTutors] = useState({});

  const loadTutorsForSubject = async (i, subject) => {
    try {
      const r = await ax.get(`/api/admin/tutors-by-subject?subject=${encodeURIComponent(subject || '')}&approved_only=true&active_only=true`);
      setSubjectTutors(prev => ({ ...prev, [i]: r.data?.items || [] }));
    } catch (_e) { setSubjectTutors(prev => ({ ...prev, [i]: tutors })); }
  };

  useEffect(() => {
    subjects.forEach((c, i) => { if (!subjectTutors[i]) loadTutorsForSubject(i, c.subject); });
  }, [subjects.length]);

  const updateSubject = (i, field, val) => {
    setSubjects(s => s.map((c, idx) => idx === i ? { ...c, [field]: val } : c));
    if (field === 'subject') loadTutorsForSubject(i, val);
  };
  const updateSlot = (i, si, field, val) => {
    setSubjects(s => s.map((c, idx) => idx === i ? { ...c, slots: c.slots.map((sl, sidx) => sidx === si ? { ...sl, [field]: val } : sl) } : c));
  };
  const addSubject = () => setSubjects(s => [...s, blankSubject()]);
  const removeSubject = (i) => setSubjects(s => s.filter((_, idx) => idx !== i));
  const addSlot = (i) => setSubjects(s => s.map((c, idx) => idx === i ? { ...c, slots: [...c.slots, blankSlot()] } : c));
  const removeSlot = (i, si) => setSubjects(s => s.map((c, idx) => idx === i ? { ...c, slots: c.slots.filter((_, sidx) => sidx !== si) } : c));

  const submit = async () => {
    if (!studentEmail) { toast.error('Pick a student'); return; }
    if (!startDate) { toast.error('Set a start date'); return; }
    if (subjects.some(c => !c.tutor_email)) { toast.error('Each subject needs a tutor'); return; }
    if (subjects.some(c => (c.slots || []).length === 0)) { toast.error('Every subject needs at least one slot'); return; }
    setBusy(true);
    try {
      const r = await ax.post('/api/admin/learning-plans', {
        student_email: studentEmail,
        parent_email: parentEmail || undefined,
        plan_name: planName || undefined,
        program, start_date: startDate, end_date: endDate || undefined,
        timezone_name: 'Australia/Melbourne',
        subjects,
      });
      const slotCount = (r.data?.plan?.subjects || []).length;
      toast.success(`Plan created · ${slotCount} session series generated`);
      onDone();
    } catch (e) { toast.error(e.response?.data?.detail || 'Create failed'); }
    finally { setBusy(false); }
  };

  return (
    <div className="fixed inset-0 bg-black/40 z-50 flex items-center justify-center p-4" onClick={onClose}>
      <div className="bg-white rounded-2xl p-5 max-w-3xl w-full max-h-[92vh] overflow-y-auto" onClick={e => e.stopPropagation()} data-testid="lp-create-modal">
        <h3 className="text-lg font-bold mb-1">Create Learning Plan</h3>
        <p className="text-[11px] text-gray-500 mb-3">Group multiple subjects, multiple weekly slots, multiple tutors under ONE plan.</p>

        <div className="grid grid-cols-2 md:grid-cols-3 gap-2 text-xs">
          <select value={studentEmail} onChange={e => setStudentEmail(e.target.value)} className="border rounded px-2 py-1.5" data-testid="lp-student">
            <option value="">— Pick student —</option>
            {students.map(s => <option key={s.email} value={s.email}>{s.name || s.email}</option>)}
          </select>
          <input placeholder="Parent email (optional)" value={parentEmail} onChange={e => setParentEmail(e.target.value)} className="border rounded px-2 py-1.5" data-testid="lp-parent" />
          <input placeholder="Plan name (e.g. July Selective Entry Plan)" value={planName} onChange={e => setPlanName(e.target.value)} className="border rounded px-2 py-1.5 col-span-2 md:col-span-1" data-testid="lp-name" />
          <input placeholder="Program" value={program} onChange={e => setProgram(e.target.value)} className="border rounded px-2 py-1.5" data-testid="lp-program" />
          <label className="border rounded px-2 py-1.5 flex items-center gap-1.5"><span className="text-[10px] text-gray-500 uppercase tracking-wider">Start</span><input type="date" value={startDate} onChange={e => setStartDate(e.target.value)} className="flex-1 outline-none" data-testid="lp-start" /></label>
          <label className="border rounded px-2 py-1.5 flex items-center gap-1.5"><span className="text-[10px] text-gray-500 uppercase tracking-wider">End</span><input type="date" value={endDate} onChange={e => setEndDate(e.target.value)} className="flex-1 outline-none" data-testid="lp-end" /></label>
        </div>

        <div className="mt-4 space-y-3">
          {subjects.map((c, i) => (
            <div key={i} className="border border-gray-200 rounded-xl p-3 bg-gray-50/50" data-testid={`lp-card-${i}`}>
              <div className="flex justify-between items-center mb-2">
                <div className="text-xs font-bold text-gray-700">Subject card #{i + 1}</div>
                {subjects.length > 1 && (
                  <button onClick={() => removeSubject(i)} className="text-[10px] text-rose-700 hover:underline inline-flex items-center gap-0.5" data-testid={`lp-remove-${i}`}>
                    <Trash2 className="h-2.5 w-2.5" /> Remove subject
                  </button>
                )}
              </div>
              <div className="grid grid-cols-2 md:grid-cols-3 gap-2 text-xs">
                <input placeholder="Subject" value={c.subject} onChange={e => updateSubject(i, 'subject', e.target.value)} className="border rounded px-2 py-1.5" data-testid={`lp-subject-${i}`} />
                <select value={c.tutor_email} onChange={e => updateSubject(i, 'tutor_email', e.target.value)} className="border rounded px-2 py-1.5 col-span-2" data-testid={`lp-tutor-${i}`}>
                  <option value="">— Approved tutors for &quot;{c.subject}&quot; —</option>
                  {(subjectTutors[i] || []).map(t => <option key={t.email} value={t.email}>{t.name || t.email}</option>)}
                  {(subjectTutors[i] || []).length === 0 && <option disabled>No approved tutors match — pick any below</option>}
                  <option disabled>──── All tutors ────</option>
                  {tutors.map(t => <option key={`all-${t.email}`} value={t.email}>{t.name || t.email}</option>)}
                </select>
                <label className="border rounded px-2 py-1.5 flex items-center gap-1.5"><span className="text-[10px] text-gray-500 uppercase tracking-wider">Duration</span><input type="number" min={15} max={180} step={5} value={c.duration_minutes} onChange={e => updateSubject(i, 'duration_minutes', parseInt(e.target.value) || 30)} className="flex-1 outline-none" /></label>
                <select value={c.package_type} onChange={e => updateSubject(i, 'package_type', e.target.value)} className="border rounded px-2 py-1.5 col-span-2" data-testid={`lp-package-${i}`}>
                  <option value="individual">Individual (AUD 55 per session)</option>
                  <option value="intensive_package">Intensive Package (AUD 250 = 4 sessions)</option>
                </select>
              </div>
              <div className="mt-3">
                <div className="text-[10px] uppercase tracking-[2px] text-gray-500 font-bold mb-1">Weekly slots ({c.slots.length})</div>
                <div className="space-y-1">
                  {c.slots.map((sl, si) => (
                    <div key={si} className="flex items-center gap-2 text-xs" data-testid={`lp-slot-${i}-${si}`}>
                      <select value={sl.day_of_week} onChange={e => updateSlot(i, si, 'day_of_week', e.target.value)} className="border rounded px-2 py-1 flex-1">
                        {DAYS.map(d => <option key={d}>{d}</option>)}
                      </select>
                      <input type="time" value={sl.start_time} onChange={e => updateSlot(i, si, 'start_time', e.target.value)} className="border rounded px-2 py-1 w-28" />
                      {c.slots.length > 1 && (
                        <button onClick={() => removeSlot(i, si)} className="text-[10px] text-rose-700 hover:underline" data-testid={`lp-slot-remove-${i}-${si}`}>
                          <Trash2 className="h-3 w-3" />
                        </button>
                      )}
                    </div>
                  ))}
                </div>
                <button onClick={() => addSlot(i)} className="mt-2 text-[10px] bg-gray-100 hover:bg-gray-200 px-2.5 py-1 rounded-full font-bold uppercase tracking-wider inline-flex items-center gap-1" data-testid={`lp-add-slot-${i}`}>
                  <Plus className="h-2.5 w-2.5" /> Add slot
                </button>
              </div>
            </div>
          ))}
          <button onClick={addSubject} className="w-full text-xs bg-gray-100 hover:bg-gray-200 px-3 py-2 rounded-full font-bold uppercase tracking-wider inline-flex items-center justify-center gap-1" data-testid="lp-add-subject"><Plus className="h-3 w-3" /> Add Subject</button>
        </div>

        <div className="flex justify-end gap-2 mt-4">
          <button onClick={onClose} className="text-xs px-3 py-1.5 border rounded-full">Cancel</button>
          <button onClick={submit} disabled={busy} className="text-xs bg-red-700 hover:bg-red-800 text-white px-4 py-2 rounded-full font-bold uppercase tracking-wider disabled:opacity-40 inline-flex items-center gap-1" data-testid="lp-submit">
            {busy ? <Loader2 className="h-3 w-3 animate-spin" /> : <Plus className="h-3 w-3" />} Create Plan
          </button>
        </div>
      </div>
    </div>
  );
};

const CustomLinkModal = ({ plan, onClose, onDone }) => {
  const [amount, setAmount] = useState(((plan.total_amount_aud_cents || 0) / 100).toFixed(2));
  const [description, setDescription] = useState(plan.plan_name || '');
  const [allowEdit, setAllowEdit] = useState(false);
  const [dueDate, setDueDate] = useState('');
  const [busy, setBusy] = useState(false);
  const [link, setLink] = useState(null);
  const [copied, setCopied] = useState(false);

  const submit = async () => {
    setBusy(true);
    try {
      const r = await ax.post(`/api/admin/learning-plans/${plan.plan_id}/custom-payment-link`, {
        amount_aud: parseFloat(amount), description, allow_payer_edit_amount: allowEdit, due_date: dueDate || undefined,
      });
      const out = r.data?.link || {};
      setLink(out.public_url || out.payment_link || null);
      toast.success(`Custom link generated · AUD ${amount}`);
      // Intentionally do NOT close modal — admin needs to copy/share the link.
      // Modal closes when admin clicks Close.
    } catch (e) {
      // 401 already handled by global interceptor
      if (e?.response?.status !== 401) toast.error(e.response?.data?.detail || 'Link failed');
    } finally { setBusy(false); }
  };

  const copy = async () => {
    if (!link) return;
    try { await navigator.clipboard.writeText(link); setCopied(true); setTimeout(() => setCopied(false), 1800); }
    catch (_e) { toast.error('Copy failed'); }
  };

  return (
    <div className="fixed inset-0 bg-black/40 z-50 flex items-center justify-center p-4" onClick={onClose}>
      <div className="bg-white rounded-2xl p-5 max-w-md w-full" onClick={e => e.stopPropagation()} data-testid="lp-custom-link-modal">
        <h3 className="text-lg font-bold mb-1">Custom Payment Link</h3>
        <p className="text-[11px] text-gray-500 mb-3">{plan.student_name} · {plan.plan_name}</p>
        <div className="space-y-2 text-xs">
          <label className="block">
            <span className="text-[10px] uppercase tracking-wider text-gray-500 font-bold">Amount (AUD)</span>
            <input type="number" step="0.01" min="1" value={amount} onChange={e => setAmount(e.target.value)} className="mt-1 w-full border rounded px-2 py-1.5" data-testid="lp-custom-amount" />
          </label>
          <label className="block">
            <span className="text-[10px] uppercase tracking-wider text-gray-500 font-bold">Description</span>
            <input value={description} onChange={e => setDescription(e.target.value)} className="mt-1 w-full border rounded px-2 py-1.5" data-testid="lp-custom-desc" />
          </label>
          <label className="block">
            <span className="text-[10px] uppercase tracking-wider text-gray-500 font-bold">Due date (optional)</span>
            <input type="date" value={dueDate} onChange={e => setDueDate(e.target.value)} className="mt-1 w-full border rounded px-2 py-1.5" />
          </label>
          <label className="flex items-center gap-2 mt-1">
            <input type="checkbox" checked={allowEdit} onChange={e => setAllowEdit(e.target.checked)} data-testid="lp-custom-allow-edit" />
            <span className="text-[11px]">Allow payer to edit amount before paying</span>
          </label>
        </div>
        {link && (
          <div className="mt-3 bg-emerald-50 border border-emerald-200 rounded p-3" data-testid="lp-custom-link-ready">
            <div className="flex items-center justify-between gap-2 mb-1">
              <div className="text-emerald-800 font-bold uppercase tracking-wider text-[10px]">Public payment link</div>
              <button onClick={copy} className="text-[10px] bg-emerald-700 hover:bg-emerald-800 text-white px-2.5 py-0.5 rounded-full font-bold uppercase tracking-wider" data-testid="lp-custom-copy">{copied ? 'Copied!' : 'Copy'}</button>
            </div>
            <a href={link} target="_blank" rel="noopener noreferrer" className="text-[11px] text-emerald-900 underline break-all" data-testid="lp-custom-link-out">{link}</a>
            <p className="text-[10px] text-emerald-800 mt-2 leading-relaxed">
              Safe to share — opens a Winhouse-hosted secure checkout. No admin login required by the payer. Link expires after due date.
            </p>
          </div>
        )}
        <div className="flex justify-end gap-2 mt-4">
          <button onClick={() => { onDone && onDone(); onClose(); }} className="text-xs px-3 py-1.5 border rounded-full" data-testid="lp-custom-close">Close</button>
          <button onClick={submit} disabled={busy} className="text-xs bg-amber-600 hover:bg-amber-700 text-white px-4 py-2 rounded-full font-bold uppercase tracking-wider disabled:opacity-40 inline-flex items-center gap-1" data-testid="lp-custom-submit">
            {busy ? <Loader2 className="h-3 w-3 animate-spin" /> : <CreditCard className="h-3 w-3" />} {link ? 'Regenerate' : 'Generate'}
          </button>
        </div>
      </div>
    </div>
  );
};

const EditPlanModal = ({ plan, onClose, onDone }) => {
  const [planName, setPlanName] = useState(plan.plan_name || '');
  const [program, setProgram] = useState(plan.program || '');
  const [endDate, setEndDate] = useState(plan.end_date || '');
  const [parentEmail, setParentEmail] = useState(plan.parent_email || '');
  const [busy, setBusy] = useState(false);

  const submit = async () => {
    setBusy(true);
    try {
      await ax.patch(`/api/admin/learning-plans/${plan.plan_id}`, {
        plan_name: planName, program, end_date: endDate || undefined, parent_email: parentEmail || undefined,
      });
      toast.success('Plan updated');
      onDone();
    } catch (_e) { toast.error('Update failed'); }
    finally { setBusy(false); }
  };

  return (
    <div className="fixed inset-0 bg-black/40 z-50 flex items-center justify-center p-4" onClick={onClose}>
      <div className="bg-white rounded-2xl p-5 max-w-md w-full" onClick={e => e.stopPropagation()} data-testid="lp-edit-modal">
        <h3 className="text-lg font-bold mb-1">Edit Learning Plan</h3>
        <p className="text-[11px] text-gray-500 mb-3">{plan.student_name}</p>
        <div className="space-y-2 text-xs">
          <input placeholder="Plan name" value={planName} onChange={e => setPlanName(e.target.value)} className="w-full border rounded px-2 py-1.5" data-testid="lp-edit-name" />
          <input placeholder="Program" value={program} onChange={e => setProgram(e.target.value)} className="w-full border rounded px-2 py-1.5" />
          <input type="date" value={endDate} onChange={e => setEndDate(e.target.value)} className="w-full border rounded px-2 py-1.5" />
          <input placeholder="Parent email" value={parentEmail} onChange={e => setParentEmail(e.target.value)} className="w-full border rounded px-2 py-1.5" />
        </div>
        <div className="flex justify-end gap-2 mt-4">
          <button onClick={onClose} className="text-xs px-3 py-1.5 border rounded-full">Cancel</button>
          <button onClick={submit} disabled={busy} className="text-xs bg-red-700 hover:bg-red-800 text-white px-4 py-2 rounded-full font-bold uppercase tracking-wider disabled:opacity-40 inline-flex items-center gap-1" data-testid="lp-edit-submit">
            {busy ? <Loader2 className="h-3 w-3 animate-spin" /> : <Edit3 className="h-3 w-3" />} Save
          </button>
        </div>
      </div>
    </div>
  );
};

const BulkDeleteModal = ({ onClose, onDone }) => {
  const [confirm, setConfirm] = useState('');
  const [busy, setBusy] = useState(false);

  const submit = async () => {
    setBusy(true);
    try {
      const r = await ax.post('/api/admin/learning-plans/bulk-delete-test', {}, {
        headers: { 'X-Confirm-Token': confirm },
      });
      const d = r.data;
      toast.success(`Cleared: ${d.learning_plans_deleted} plans · ${d.booking_series_deleted} series · ${d.non_completed_bookings_deleted} pending bookings`);
      onDone();
    } catch (e) { toast.error(e.response?.data?.detail || 'Reset failed'); }
    finally { setBusy(false); }
  };

  return (
    <div className="fixed inset-0 bg-black/40 z-50 flex items-center justify-center p-4" onClick={onClose}>
      <div className="bg-white rounded-2xl p-5 max-w-md w-full border-2 border-rose-300" onClick={e => e.stopPropagation()} data-testid="lp-bulk-modal">
        <h3 className="text-lg font-bold mb-1 text-rose-900 flex items-center gap-2"><AlertTriangle className="h-5 w-5" /> Clear Test Plans</h3>
        <p className="text-[12px] text-gray-700 mb-2">
          This permanently deletes <strong>ALL</strong> Learning Plans, Booking Series and pending bookings.
        </p>
        <ul className="text-[11px] text-gray-600 mb-3 space-y-0.5 list-disc pl-4">
          <li>Preserves: students, tutors, paid payments, Learning Centre progress, completed classroom history.</li>
          <li>Removes: all plans, all series, all bookings that are <em>not</em> completed.</li>
        </ul>
        <label className="text-[10px] uppercase tracking-wider text-gray-500 font-bold">Type <span className="font-mono bg-rose-50 px-1.5 py-0.5 rounded">DELETE TEST PLANS</span> to confirm</label>
        <input value={confirm} onChange={e => setConfirm(e.target.value)} className="mt-1 w-full border-2 border-rose-200 rounded px-2 py-1.5 text-xs font-mono" data-testid="lp-bulk-confirm" placeholder="DELETE TEST PLANS" />
        <div className="flex justify-end gap-2 mt-4">
          <button onClick={onClose} className="text-xs px-3 py-1.5 border rounded-full">Cancel</button>
          <button onClick={submit} disabled={busy || confirm !== 'DELETE TEST PLANS'} className="text-xs bg-rose-700 hover:bg-rose-800 text-white px-4 py-2 rounded-full font-bold uppercase tracking-wider disabled:opacity-40 inline-flex items-center gap-1" data-testid="lp-bulk-submit">
            {busy ? <Loader2 className="h-3 w-3 animate-spin" /> : <Trash2 className="h-3 w-3" />} Reset
          </button>
        </div>
      </div>
    </div>
  );
};

export default AdminLearningPlansPanel;
